import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { connect } from 'react-redux';
import { compose } from 'redux';

import {
  IconSpinner,
  LayoutSingleColumn,
  ListingCard,
  NamedLink,
  Page,
} from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { createHandymanSocialSharing } from '../../util/handymanSocialSharing';
import { buildHandymanLocalBusinessSchema } from '../../util/handymanLocalBusinessSchema';
import { propTypes } from '../../util/types';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { createHandymanMapStateToProps } from '../HandymanPage/handymanPageState';
import css from '../HandymanPage/HandymanPage.module.css';
import {
  getTorontoHandymanServiceConfig,
  torontoHandymanServiceLinks,
} from './HandymanTorontoServicePage.config';

export const HandymanTorontoServicePageComponent = props => {
  const { listings, params, queryInProgress, queryListingsError } = props;
  const serviceConfig = getTorontoHandymanServiceConfig(params?.serviceSlug);
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const rootUrl = config?.marketplaceRootURL || 'https://fixittask.com';

  if (!serviceConfig) {
    return <NotFoundPage />;
  }
  const listingRenderSizes = [
    '(max-width: 767px) 100vw',
    '(max-width: 1023px) 50vw',
    '33vw',
  ].join(', ');

  const createSearchPath = searchParams =>
    createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams);

  const browseAllPath = createSearchPath({});
  const relatedLinks = [
    { label: 'Toronto handyman services', href: '/handyman/toronto' },
    { label: 'Ontario handyman services', href: '/handyman' },
  ];
  const commonSearchLinks = torontoHandymanServiceLinks.filter(
    item => item.href !== serviceConfig.path
  );
  const primaryListings = listings.slice(0, 9);
  const teaserListings = listings.slice(9, 12);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: serviceConfig.h1,
      description: serviceConfig.metaDescription,
      url: `${rootUrl}${serviceConfig.path}`,
    },
    buildHandymanLocalBusinessSchema({
      rootUrl,
      pageUrl: `${rootUrl}${serviceConfig.path}`,
      name: serviceConfig.h1,
      description: serviceConfig.metaDescription,
      city: 'Toronto',
      region: 'Ontario',
    }),
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: rootUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Handyman',
          item: `${rootUrl}/handyman`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Toronto',
          item: `${rootUrl}/handyman/toronto`,
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: serviceConfig.pageName,
          item: `${rootUrl}${serviceConfig.path}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: serviceConfig.faqs.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ];

  return (
    <Page
      title={serviceConfig.metaTitle}
      description={serviceConfig.metaDescription}
      socialSharing={createHandymanSocialSharing(
        rootUrl,
        serviceConfig.metaTitle,
        serviceConfig.metaDescription
      )}
      schema={schema}
      className={css.root}
    >
      <Helmet>
        <link rel="canonical" href={`${rootUrl}${serviceConfig.path}`} />
      </Helmet>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.page}>
          <section className={css.hero} style={{ backgroundImage: `url(${serviceConfig.heroImage})` }}>
            <div className={css.heroOverlay}>
              <div className={css.heroContent}>
                <nav className={css.breadcrumbs} aria-label="Breadcrumb">
                  <NamedLink name="LandingPage" className={css.breadcrumbLink}>
                    Home
                  </NamedLink>
                  <span aria-hidden="true">/</span>
                  <NamedLink name="HandymanPage" className={css.breadcrumbLink}>
                    Handyman
                  </NamedLink>
                  <span aria-hidden="true">/</span>
                  <NamedLink name="HandymanTorontoPage" className={css.breadcrumbLink}>
                    Toronto
                  </NamedLink>
                  <span aria-hidden="true">/</span>
                  <span>{serviceConfig.pageName}</span>
                </nav>

                <p className={css.eyebrow}>{serviceConfig.eyebrow}</p>
                <h1 className={css.title}>{serviceConfig.h1}</h1>
                <p className={css.lead}>{serviceConfig.lead}</p>
              </div>
            </div>
          </section>

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Live marketplace listings</p>
              <h2 className={css.sectionTitle}>{serviceConfig.listingHeading}</h2>
              <p className={css.sectionText}>{serviceConfig.listingText}</p>
            </div>

            {queryInProgress ? (
              <div className={css.listingsState}>
                <IconSpinner className={css.loader} />
              </div>
            ) : queryListingsError ? (
              <div className={css.listingsState}>
                <h3 className={css.cardTitle}>{serviceConfig.errorTitle}</h3>
                <p className={css.cardText}>{serviceConfig.errorText}</p>
                <a className={css.secondaryButton} href={browseAllPath} rel="nofollow">
                  Browse all listings
                </a>
              </div>
            ) : listings.length > 0 ? (
              <>
                <div className={css.listingGrid}>
                  {primaryListings.map(listing => (
                    <ListingCard
                      className={css.marketplaceListingCard}
                      key={listing.id.uuid}
                      listing={listing}
                      renderSizes={listingRenderSizes}
                    />
                  ))}
                </div>

                {teaserListings.length > 0 ? (
                  <div className={css.teaserWrap}>
                    <div className={css.teaserGrid}>
                      {teaserListings.map(listing => (
                        <div className={css.teaserCardShell} key={listing.id.uuid}>
                          <ListingCard
                            className={css.marketplaceListingCard}
                            listing={listing}
                            renderSizes={listingRenderSizes}
                          />
                        </div>
                      ))}
                    </div>

                    <div className={css.teaserCta}>
                      <h3 className={css.teaserTitle}>{serviceConfig.teaserTitle}</h3>
                      <p className={css.teaserText}>{serviceConfig.teaserText}</p>
                      <a className={css.primaryButton} href={browseAllPath} rel="nofollow">
                        {serviceConfig.browseButtonLabel}
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className={css.inlineCta}>
                    <a className={css.primaryButton} href={browseAllPath} rel="nofollow">
                      {serviceConfig.browseButtonLabel}
                    </a>
                  </div>
                )}
              </>
            ) : (
              <div className={css.listingsState}>
                <h3 className={css.cardTitle}>{serviceConfig.emptyTitle}</h3>
                <p className={css.cardText}>{serviceConfig.emptyText}</p>
                <a className={css.secondaryButton} href={browseAllPath} rel="nofollow">
                  Browse all listings
                </a>
              </div>
            )}
          </section>

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Booking guide</p>
              <h2 className={css.sectionTitle}>{serviceConfig.bookingHeading}</h2>
              <p className={css.sectionText}>{serviceConfig.bookingText}</p>
            </div>

            <div className={css.contentGrid}>
              {serviceConfig.contentSections.map(section => (
                <article className={css.contentCard} key={section.title}>
                  <h3 className={css.contentTitle}>{section.title}</h3>
                  {section.paragraphs.map(paragraph => (
                    <p className={css.contentParagraph} key={paragraph}>
                      {paragraph}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </section>

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Related handyman pages</p>
              <h2 className={css.sectionTitle}>Browse more handyman pages</h2>
              <p className={css.sectionText}>
                Use these links to go back to the main Toronto handyman page or the Ontario
                handyman page if you want to compare more services.
              </p>
            </div>

            <div className={css.pillGrid}>
              {relatedLinks.map(item => (
                <a className={css.pillLink} href={item.href} key={item.label}>
                  {item.label}
                </a>
              ))}
            </div>
          </section>

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Popular services in Toronto</p>
              <h2 className={css.sectionTitle}>More handyman services in Toronto</h2>
              <p className={css.sectionText}>
                Browse other handyman services Toronto customers often book.
              </p>
            </div>

            <div className={css.pillGrid}>
              {commonSearchLinks.map(item => (
                <a className={css.pillLink} href={item.href} key={item.label}>
                  {item.label}
                </a>
              ))}
            </div>
          </section>

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>FAQ</p>
              <h2 className={css.sectionTitle}>Questions customers usually have</h2>
            </div>

            <div className={css.faqGrid}>
              {serviceConfig.faqs.map(item => (
                <article className={css.faqCard} key={item.question}>
                  <h3 className={css.cardTitle}>{item.question}</h3>
                  <p className={css.cardText}>{item.answer}</p>
                </article>
              ))}
            </div>
          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

HandymanTorontoServicePageComponent.defaultProps = {
  listings: [],
  queryListingsError: null,
};

const { arrayOf, bool, shape, string } = PropTypes;

HandymanTorontoServicePageComponent.propTypes = {
  listings: arrayOf(propTypes.listing),
  params: shape({
    serviceSlug: string,
  }).isRequired,
  queryInProgress: bool.isRequired,
  queryListingsError: propTypes.error,
};

const HandymanTorontoServicePage = compose(
  connect(createHandymanMapStateToProps('HandymanTorontoServicePage'))
)(HandymanTorontoServicePageComponent);

export default HandymanTorontoServicePage;
